import NumberUtils from '../utils/number';

const pad = (input: string, length: number = 2, padString: string = '0') => {
  const missing = length - input.length;
  const finalInput = input.toString();

  let aChar = '';
  let prefix = '';
  let i = 0;

  if (missing <= 0) {
    return finalInput;
  }

  aChar = padString.charAt(0);

  for (; i < missing; i += 1) {
    prefix += aChar;
  }

  return prefix + input;
};

const { isRealFloat } = NumberUtils;

export const toFixed = (value: number, precision = 2) => {
  let power = 0;
  const finalPrecision = Math.abs(precision);
  power = 10 ** finalPrecision;
  return (Math.round(value * power) / power).toFixed(finalPrecision);
};

type FormatNumberOptions = {
  decimalSeparator: string;
  precision: number;
  thousandSeparator: string;
  groupLength: number;
  abbreviate: boolean;
};
const formatNumber = (
  value: number,
  { decimalSeparator, precision, thousandSeparator, groupLength, abbreviate }: FormatNumberOptions,
): string => {
  const result = [];

  let str = '';
  let intNum = '';
  let intLength = 0;
  let floatNum = '';
  let floatLength = 0;

  if (abbreviate) {
    str = `${value}`;
  } else {
    str = toFixed(value, precision);
  }

  const pos = str.indexOf('.');

  if (pos !== -1) {
    intNum = str.substring(0, pos);
    floatNum = str.substring(pos + 1);

    floatNum += pad('', precision - floatNum.length, '0');

    intLength = intNum.length;
    floatLength = abbreviate ? groupLength - intLength : precision;

    if (floatLength) {
      floatNum = floatNum.substring(0, floatLength);
    } else {
      floatNum = '';
    }
  } else {
    intNum = str;
    floatNum = '';
    intLength = str.length;
  }

  intNum = pad(intNum, intLength + (groupLength - (intLength % groupLength)), ' ');

  const max = intNum.length;

  for (let i = 0; i < max; i += groupLength) {
    if (i > 0) {
      result.push(thousandSeparator);
    }
    result.push(intNum.substr(i, groupLength));
  }

  if (floatNum) {
    result.push(decimalSeparator, floatNum);
  }

  return result.join('').replace(new RegExp(`^[\\s|${thousandSeparator}]+`), '');
};

export type NumberFormatterConfigurationType = {
  patterns: {
    standard: Array<string>;
    amount: Array<string>;
  };
  group: {
    separator: string;
    length: number;
    abbr: Array<string>;
  };
  decimal: {
    separator: string;
    length: number;
  };
  ordinal: (n: number) => string;
};

interface INumberFormatter {
  setConfiguration: (conf: NumberFormatterConfigurationType) => void;
  ordinal: (n: number) => string;
  formatOdd: (value: number) => string;
  formatOddForBasketButtonUsage: (value: number) => string;
  format: (
    value: number,
    currency: string,
    abbreviate?: boolean,
    decimalLength?: number,
    trimDecimalPartWhenPossible?: boolean,
  ) => string;
  abbreviate: (value: number, currency: string, decimalLength: number) => string;
}

class NumberFormatter implements INumberFormatter {
  private configuration: NumberFormatterConfigurationType = {
    patterns: {
      standard: ['-n', 'n'],
      amount: ['-n S', 'n S'],
    },
    group: {
      separator: ' ',
      length: 3,
      abbr: ['', 'k', 'M', 'Md'],
    },
    decimal: {
      separator: ',',
      length: 2,
    },
    ordinal: (number) => number + (number === 1 ? 'er' : 'e'),
  };

  setConfiguration = (configuration: NumberFormatterConfigurationType) => {
    this.configuration = configuration;
  };

  ordinal = (value: number) => {
    const finalValue = value | 0;
    if (value < 1) {
      throw new Error(`first argument must be a positive number: ${finalValue}`);
    }
    return this.configuration.ordinal.call(null, finalValue);
  };

  formatOdd = (value: number) => {
    if (typeof value !== 'number') {
      throw new Error(`first argument must be a number: ${value}`);
    }

    const { group, decimal, patterns } = this.configuration;
    const result = formatNumber(Math.abs(value), {
      decimalSeparator: decimal.separator,
      precision: !isRealFloat(value) && value >= 10 ? 0 : decimal.length,
      thousandSeparator: group.separator,
      groupLength: group.length,
      abbreviate: false,
    });

    const base = patterns.standard;
    const pattern = value < 0 ? base[0] : base[1];

    return pattern.replace('n', result);
  };

  formatOddForBasketButtonUsage = (value: number) => {
    if (typeof value !== 'number') {
      throw new Error(`first argument must be a number: ${value}`);
    }

    const { group, decimal, patterns } = this.configuration;
    const result = formatNumber(Math.abs(value), {
      decimalSeparator: decimal.separator,
      precision: !isRealFloat(value) && value >= 1000 ? 0 : decimal.length,
      thousandSeparator: group.separator,
      groupLength: group.length,
      abbreviate: false,
    });

    const base = patterns.standard;
    const pattern = value < 0 ? base[0] : base[1];

    return pattern.replace('n', result);
  };

  format = (
    value: number,
    currency: string,
    abbreviate = false,
    decimalLength = this.configuration.decimal.length,
    trimDecimalPartWhenPossible = false,
  ) => {
    if (typeof value !== 'number') {
      throw new Error(`first argument must be a number: ${value}`);
    }

    const { group, decimal, patterns } = this.configuration;
    const result = formatNumber(Math.abs(value), {
      decimalSeparator: decimal.separator,
      precision: trimDecimalPartWhenPossible && !isRealFloat(value) ? 0 : decimalLength,
      thousandSeparator: group.separator,
      groupLength: group.length,
      abbreviate,
    });
    const base = currency ? patterns.amount : patterns.standard;
    const pattern = value < 0 ? base[0] : base[1];

    return pattern.replace('S', currency || '').replace('n', result);
  };

  abbreviate = (value: number, currency: string, decimalLength = this.configuration.decimal.length) => {
    const config = this.configuration;
    const patterns = config.patterns;
    const base = currency ? patterns.amount : patterns.standard;
    const pattern = value < 0 ? base[0] : base[1];

    const abbreviateNumber = (value: number, decimalLength: number) => {
      const config = this.configuration;
      const group = config.group;
      const decimal = config.decimal;
      const decimalLen = decimalLength === undefined ? decimal.length : decimalLength;
      let i = 0;
      let result = '';

      // Abrège la valeur
      value = Math.abs(value);
      while (undefined !== group?.abbr[i + 1] && value >= 1000) {
        value /= 1000;
        i++;
      }

      // Formatte le nombre et ajoute le suffix
      result = formatNumber(value, {
        decimalSeparator: decimal.separator,
        precision: decimalLen,
        thousandSeparator: group.separator,
        groupLength: group.length,
        abbreviate: !decimalLength,
      });
      return result + (group.abbr[i] || '');
    };

    let result = '';
    result = abbreviateNumber(value, decimalLength);

    return pattern.replace('S', currency || '').replace('n', result);
  };
}

const NumberFormatterInstance = new NumberFormatter();

export default NumberFormatterInstance;
